/* ==========================================================================
   PRODUCT
   Variant selection, the media gallery, the zoom viewer and the sticky
   mobile add-to-cart bar.

   Variant changes are resolved from an embedded JSON blob rather than a
   network round-trip, so the price, availability and gallery update in the
   same frame as the click.
   ========================================================================== */

(function () {
  'use strict';

  const Theme = window.Theme;

  /* ========================================================================
     <variant-picker>
     ======================================================================== */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]') || document;
      this.variants = this.readVariants();
      this.optionInputs = Array.from(this.querySelectorAll('input[type="radio"], select'));

      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => this.onChange());
      });

      // A shopper landing on a URL with ?variant= should see that variant
      // selected; Liquid has already checked the right inputs, so this only
      // needs to publish the derived state.
      this.updateDerivedState({ silent: true });
    }

    readVariants() {
      const script = this.querySelector('[data-variant-data]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    /** The option values currently chosen, in Shopify's option order. */
    get selectedOptions() {
      const values = [];
      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const position = Number(group.dataset.optionPosition) - 1;
        const checked = group.querySelector('input[type="radio"]:checked');
        const select = group.querySelector('select');
        values[position] = checked ? checked.value : select ? select.value : null;
      });
      return values;
    }

    get currentVariant() {
      // A product with only the default variant has no option groups at all,
      // so there is nothing to match on — the single variant is the answer.
      if (this.querySelectorAll('[data-option-position]').length === 0) {
        return this.variants[0] || null;
      }

      const selected = this.selectedOptions;
      if (selected.some((value) => value == null)) return null;
      return (
        this.variants.find((variant) =>
          variant.options.every((option, index) => option === selected[index])
        ) || null
      );
    }

    onChange() {
      const variant = this.currentVariant;
      this.updateDerivedState();

      if (variant) {
        this.updateUrl(variant);
        this.updateGallery(variant);
      }
      Theme.emit('variant:change', { variant, picker: this });
    }

    updateDerivedState(options) {
      const variant = this.currentVariant;

      this.markSelectedLabels();
      this.markUnavailableCombinations();
      this.updateIdInputs(variant);
      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateButton(variant);
      this.updateStickyBar(variant);

      if (!options || !options.silent) {
        const notice = this.root.querySelector('[data-option-error]');
        if (notice && variant) notice.hidden = true;
      }
    }

    /* Selected state on the visible label, since the input itself is hidden. */
    markSelectedLabels() {
      this.querySelectorAll('.option-value, .option-swatch').forEach((label) => {
        const input = label.querySelector('input');
        if (input) label.classList.toggle('is-selected', input.checked);
      });

      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        const select = group.querySelector('select');
        const value = checked ? checked.value : select ? select.value : null;

        const display = group.querySelector('[data-option-selected]');
        if (display) display.textContent = value || '';

        group.dataset.optionUnset = value ? 'false' : 'true';
      });
    }

    /**
     * Cross out option values that cannot be reached from the rest of the
     * current selection — the shopper sees which sizes exist in this colour
     * before clicking, rather than after.
     */
    markUnavailableCombinations() {
      const selected = this.selectedOptions;

      this.querySelectorAll('[data-option-position]').forEach((group) => {
        const position = Number(group.dataset.optionPosition) - 1;

        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const candidate = selected.slice();
          candidate[position] = input.value;

          const match = this.variants.filter((variant) =>
            variant.options.every(
              (option, index) =>
                index === position || candidate[index] == null || option === candidate[index]
            )
          );

          const exists = match.some((variant) => variant.options[position] === input.value);
          const available = match.some(
            (variant) => variant.options[position] === input.value && variant.available
          );

          const label = input.closest('.option-value, .option-swatch');
          if (!label) return;

          label.classList.toggle('is-unavailable', !available);
          input.disabled = !exists;

          const note = label.querySelector('[data-option-note]');
          if (note) {
            note.textContent = available ? '' : Theme.strings.soldOut || 'Sold out';
          }
        });

        group.querySelectorAll('select option').forEach((option) => {
          const candidate = selected.slice();
          candidate[position] = option.value;
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every(
                (value, index) => candidate[index] == null || value === candidate[index]
              )
          );
          option.dataset.available = String(available);
        });
      });
    }

    updateIdInputs(variant) {
      this.root.querySelectorAll('[data-variant-id-input]').forEach((input) => {
        input.value = variant ? variant.id : '';
      });
    }

    updateUrl(variant) {
      if (this.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    updatePrice(variant) {
      const target = this.root.querySelector('[data-price-target]');
      if (!target) return;

      if (!variant) {
        target.setAttribute('aria-busy', 'false');
        return;
      }

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      target.classList.toggle('price--on-sale', Boolean(onSale));

      const current = target.querySelector('[data-price-current]');
      if (current) current.textContent = Theme.formatMoney(variant.price);

      const was = target.querySelector('[data-price-was]');
      if (was) {
        was.hidden = !onSale;
        if (onSale) was.textContent = Theme.formatMoney(variant.compare_at_price);
      }

      const discount = target.querySelector('[data-price-discount]');
      if (discount) {
        discount.hidden = !onSale;
        if (onSale) {
          const percent = Math.round(
            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
          );
          const saved = Theme.formatMoney(variant.compare_at_price - variant.price);
          discount.textContent =
            '−' + percent + '% · ' + (Theme.strings.save || 'Save') + ' ' + saved;
        }
      }

      const sku = this.root.querySelector('[data-variant-sku]');
      if (sku) {
        sku.textContent = variant.sku || '';
        const wrap = sku.closest('[data-variant-sku-wrap]');
        if (wrap) wrap.hidden = !variant.sku;
      }
    }

    updateAvailability(variant) {
      const target = this.root.querySelector('[data-stock-target]');
      if (!target) return;

      const threshold = Number(this.dataset.lowStockThreshold || 0);

      target.className = 'pdp__stock';

      if (!variant) {
        target.textContent = '';
        return;
      }

      if (!variant.available) {
        target.classList.add('pdp__stock--out');
        target.textContent = Theme.strings.soldOut || 'Sold out';
        return;
      }

      const qty = variant.inventory_quantity;
      const tracked = variant.inventory_management === 'shopify';

      if (tracked && threshold > 0 && qty > 0 && qty <= threshold) {
        target.classList.add('pdp__stock--low');
        target.textContent = (Theme.strings.lowStock || 'Only {{ count }} left').replace(
          '{{ count }}',
          qty
        );
      } else {
        target.classList.add('pdp__stock--in');
        target.textContent = Theme.strings.inStock || 'In stock';
      }
    }

    updateButton(variant) {
      this.root.querySelectorAll('[data-add-button]').forEach((button) => {
        const label = button.querySelector('[data-add-label]') || button;

        if (!variant) {
          button.disabled = false;
          button.setAttribute('aria-disabled', 'false');
          label.textContent = Theme.strings.chooseOptions || 'Select options';
        } else if (!variant.available) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          label.textContent = Theme.strings.soldOut || 'Sold out';
        } else {
          button.disabled = false;
          button.setAttribute('aria-disabled', 'false');
          label.textContent = Theme.strings.addToCart || 'Add to bag';
        }
      });

      // Shopify's dynamic checkout button cannot sell an unavailable variant.
      const payment = this.root.querySelector('.shopify-payment-button');
      if (payment) payment.hidden = !variant || !variant.available;

      const soldOutNote = this.root.querySelector('[data-sold-out-note]');
      if (soldOutNote) soldOutNote.hidden = !variant || variant.available;
    }

    updateStickyBar(variant) {
      const bar = document.querySelector('sticky-atc');
      if (bar && typeof bar.sync === 'function') bar.sync(variant);
    }

    /** Bring the variant's own photography to the front of the gallery. */
    updateGallery(variant) {
      const gallery = this.root.querySelector('product-gallery');
      if (!gallery || typeof gallery.showVariant !== 'function') return;
      gallery.showVariant(variant);
    }
  }

  customElements.define('variant-picker', VariantPicker);

  /* ========================================================================
     <product-gallery>
     ======================================================================== */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-gallery-rail]');
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.active = 0;

      if (this.rail) {
        let frame = 0;
        this.rail.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => this.syncActive());
          },
          { passive: true }
        );
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.scrollToSlide(index));
      });

      this.addEventListener('click', (event) => {
        const slide = event.target.closest('[data-gallery-slide]');
        if (slide && slide.dataset.mediaType === 'image') {
          this.openZoom(this.visibleSlides().indexOf(slide));
        }
        const zoomBtn = event.target.closest('[data-gallery-zoom]');
        if (zoomBtn) this.openZoom(this.active);
      });
    }

    visibleSlides() {
      return this.slides.filter((slide) => !slide.hidden);
    }

    syncActive() {
      if (!this.rail) return;
      const width = this.rail.clientWidth || 1;
      const index = Math.round(this.rail.scrollLeft / width);
      if (index === this.active) return;
      this.active = index;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }

    scrollToSlide(index) {
      if (!this.rail) return;
      this.rail.scrollTo({ left: index * this.rail.clientWidth, behavior: 'smooth' });
    }

    /**
     * Filter the gallery to the chosen colourway when the merchant has grouped
     * media by colour, otherwise just bring the variant's featured image up.
     */
    showVariant(variant) {
      if (!variant) return;

      const colorValue = (variant.color || '').toLowerCase();
      const grouped = this.dataset.groupByColor === 'true' && colorValue;

      let anyMatch = false;

      if (grouped) {
        this.slides.forEach((slide) => {
          const alt = (slide.dataset.mediaColor || '').toLowerCase();
          const match = !alt || alt === colorValue;
          slide.hidden = !match;
          if (match) anyMatch = true;
        });
        // Never hide the whole gallery because a colour has no dedicated shot.
        if (!anyMatch) this.slides.forEach((slide) => (slide.hidden = false));
      }

      this.dots.forEach((dot, index) => {
        const slide = this.slides[index];
        dot.hidden = slide ? slide.hidden : true;
      });

      if (variant.featured_media_id) {
        const target = this.visibleSlides().findIndex(
          (slide) => Number(slide.dataset.mediaId) === Number(variant.featured_media_id)
        );
        if (target > -1) {
          this.scrollToSlide(target);
          if (window.matchMedia('(min-width: 1024px)').matches) {
            const slide = this.visibleSlides()[target];
            if (slide && target > 0) {
              slide.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }
      }
    }

    openZoom(index) {
      const zoom = document.querySelector('image-zoom');
      if (!zoom) return;
      zoom.show(this.visibleSlides(), Math.max(0, index));
    }
  }

  customElements.define('product-gallery', ProductGallery);

  /* ========================================================================
     <image-zoom> — full-screen viewer with click-to-magnify panning.
     ======================================================================== */

  class ImageZoom extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('theme-overlay');
      this.stage = this.querySelector('[data-zoom-stage]');
      this.image = this.querySelector('[data-zoom-image]');
      this.thumbs = this.querySelector('[data-zoom-thumbs]');
      this.counter = this.querySelector('[data-zoom-counter]');
      this.sources = [];
      this.index = 0;
      this.magnified = false;

      if (this.stage) {
        this.stage.addEventListener('click', (event) => this.toggleMagnify(event));
        this.stage.addEventListener('mousemove', (event) => {
          if (this.magnified) this.setOrigin(event);
        });
      }

      this.querySelectorAll('[data-zoom-nav]').forEach((button) => {
        button.addEventListener('click', () =>
          this.go(this.index + Number(button.dataset.zoomNav))
        );
      });

      document.addEventListener('keydown', (event) => {
        if (!this.overlay || !this.overlay.classList.contains('is-open')) return;
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
      });
    }

    show(slides, index) {
      this.sources = slides
        .filter((slide) => slide.dataset.mediaType === 'image')
        .map((slide) => ({
          src: slide.dataset.zoomSrc || slide.querySelector('img').currentSrc,
          alt: slide.querySelector('img') ? slide.querySelector('img').alt : '',
        }));

      if (this.sources.length === 0) return;

      this.renderThumbs();
      this.go(index);
      if (this.overlay) this.overlay.open();
    }

    renderThumbs() {
      if (!this.thumbs) return;
      this.thumbs.innerHTML = '';
      this.sources.forEach((source, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'zoom__thumb';
        button.setAttribute('aria-label', (Theme.strings.viewImage || 'View image') + ' ' + (index + 1));
        const img = document.createElement('img');
        img.src = source.src;
        img.alt = '';
        img.loading = 'lazy';
        button.appendChild(img);
        button.addEventListener('click', () => this.go(index));
        this.thumbs.appendChild(button);
      });
      this.thumbs.hidden = this.sources.length < 2;
      const nav = this.querySelector('[data-zoom-thumbs-wrap]');
      if (nav) nav.hidden = this.sources.length < 2;
    }

    go(next) {
      const total = this.sources.length;
      if (total === 0) return;
      this.index = ((next % total) + total) % total;

      this.resetMagnify();

      const source = this.sources[this.index];
      if (this.image) {
        this.image.src = source.src;
        this.image.alt = source.alt;
      }
      if (this.counter) {
        this.counter.textContent = this.index + 1 + ' / ' + total;
      }
      if (this.thumbs) {
        Array.from(this.thumbs.children).forEach((thumb, i) =>
          thumb.classList.toggle('is-active', i === this.index)
        );
      }
    }

    toggleMagnify(event) {
      this.magnified = !this.magnified;
      this.stage.classList.toggle('is-magnified', this.magnified);
      if (this.magnified) this.setOrigin(event);
      else this.image.style.transform = 'scale(1)';
    }

    setOrigin(event) {
      const rect = this.stage.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      this.image.style.transformOrigin = x + '% ' + y + '%';
      this.image.style.transform = 'scale(2.1)';
    }

    resetMagnify() {
      this.magnified = false;
      if (this.stage) this.stage.classList.remove('is-magnified');
      if (this.image) this.image.style.transform = 'scale(1)';
    }
  }

  customElements.define('image-zoom', ImageZoom);

  /* ========================================================================
     <sticky-atc> — appears once the real add-to-cart button scrolls away.
     ======================================================================== */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.anchor = document.querySelector('[data-atc-anchor]');
      this.priceTarget = this.querySelector('[data-sticky-price]');
      this.button = this.querySelector('[data-sticky-button]');

      if (this.button) {
        this.button.addEventListener('click', () => {
          // Hand off to the real form so validation and loading states are
          // written in exactly one place.
          const form = document.querySelector('[data-product-root] product-form form');
          if (form) form.requestSubmit();
        });
      }

      if (!this.anchor || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', past);
          });
        },
        { threshold: 0 }
      );
      this.observer.observe(this.anchor);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    sync(variant) {
      if (!variant) return;
      if (this.priceTarget) this.priceTarget.textContent = Theme.formatMoney(variant.price);
      if (this.button) {
        const label = this.button.querySelector('[data-add-label]') || this.button;
        this.button.disabled = !variant.available;
        label.textContent = variant.available
          ? Theme.strings.addToCart || 'Add to bag'
          : Theme.strings.soldOut || 'Sold out';
      }
    }
  }

  customElements.define('sticky-atc', StickyAtc);

  /* ========================================================================
     <recently-viewed> — a local trail, rendered from the Search API.
     ======================================================================== */

  const RECENT_KEY = 'theme:recently-viewed';

  Theme.trackRecentlyViewed = function (handle) {
    if (!handle) return;
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      const next = [handle].concat(list.filter((item) => item !== handle)).slice(0, 12);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (error) {
      /* Storage unavailable — the feature simply does not appear. */
    }
  };

  Theme.recentlyViewed = function () {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  };
})();
